/**
 * Centralized API endpoint configuration.
 *
 * All external service URLs should be defined here so they can be
 * swapped for different environments or self-hosted instances.
 */

const isDev = import.meta.env.DEV;

/** OpenReel cloud services */
export const OPENREEL_CLOUD_URL = isDev
  ? "http://localhost:8787"
  : "https://api.tryflshy.com";

/** OpenReel transcription / TTS service */
export const OPENREEL_TTS_URL =
  import.meta.env.VITE_OPENREEL_TTS_URL ||
  (isDev ? "http://127.0.0.1:18000" : "https://api.tryflshy.com");

/**
 * Caption transcription service.
 *
 * Served by the AI Lambda behind api.tryflshy.com in prod, and by the auth
 * dev harness (which also mounts the AI routes) locally, so cloud captions
 * can be exercised with nothing but a GROQ_API_KEY or OPENAI_API_KEY in the
 * harness env.
 */
export const OPENREEL_TRANSCRIBE_URL =
  import.meta.env.VITE_TRANSCRIBE_URL ||
  (isDev ? "http://localhost:8791" : "https://api.tryflshy.com");

/**
 * Auth service: OAuth start/callback, magic links, session and onboarding.
 *
 * The browser sends the session cookie to this origin, so it must be an exact
 * origin we control. Never point it at a host derived from user input.
 */
export const OPENREEL_AUTH_URL =
  import.meta.env.VITE_AUTH_URL ||
  (isDev ? "http://localhost:8791" : "https://api.tryflshy.com");

/**
 * Connected Accounts service: connect / list / disconnect third-party OAuth
 * providers whose tokens flshy stores server-side. This is a separate service
 * from OPENREEL_AUTH_URL (that one is sign-in). All token exchange and storage
 * happens on this origin; the browser only ever sees the STATUS DTOs.
 *
 * In dev this points at the local connections harness on :8790 (run it with the
 * connections package's dev-server). In prod it moves to the AWS stack behind
 * api.tryflshy.com; override with VITE_CONNECTIONS_URL for a Function URL.
 *
 * The browser sends the session cookie and the CSRF double-submit token to this
 * origin, so it must be an exact origin we control. Never derive it from input.
 */
export const OPENREEL_CONNECTIONS_URL =
  import.meta.env.VITE_CONNECTIONS_URL ||
  (isDev ? "http://localhost:8790" : "https://api.tryflshy.com");

/**
 * Third-party API base URLs.
 * These are used by the api-proxy service in dev mode (direct calls)
 * and by the Cloudflare Pages Function proxy in production.
 * Application code should use apiFetch() from services/api-proxy.ts
 * instead of importing these directly.
 */
